import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EdHubDetailShell } from "../components/education/EdHubDetailShell";
import { useResolvedImageUrl } from "../hooks/useResolvedImageUrl";
import { useAnalytics } from "../lib/analytics";
import { formatToSegment } from "../lib/edhub/formatToSegment";
import {
	type DerivedPodcastMeta,
	derivePodcastMeta,
} from "../lib/edhub/podcastMeta";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type Article,
	type CardDeck,
	type EducationHubArticleDetail,
	fetchArticle,
	fetchArticleDetail,
	fetchCardDeck,
	resolveMediaUrl,
	savePodcastProgress,
} from "../services/educationService";
import styles from "./EducationPodcast.module.css";

const SPEEDS = [1, 1.5, 2] as const;

// Inline glyphs lifted from Figma node 2335:73149 / 2335:73176 so the player
// matches the design exactly (purple #5E60BF on the white pill; white on the
// cover scrim). aria-hidden — each control's <button> carries the label.
const IconClock = () => (
	<svg
		width="11"
		height="11"
		viewBox="0 0 10 10"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#fff"
			d="M4.9969 0C2.23861 0 0 2.23861 0 4.9969C0 7.75518 2.23861 9.99379 4.9969 9.99379C7.75518 9.99379 9.99379 7.75518 9.99379 4.9969C9.99379 2.23861 7.75518 0 4.9969 0ZM4.9969 8.99441C2.79326 8.99441 0.999379 7.20053 0.999379 4.9969C0.999379 2.79326 2.79326 0.999379 4.9969 0.999379C7.20053 0.999379 8.99441 2.79326 8.99441 4.9969C8.99441 7.20053 7.20053 8.99441 4.9969 8.99441ZM5.24674 2.49845H4.49721V5.49659L7.12058 7.07061L7.49534 6.45599L5.24674 5.12182V2.49845Z"
		/>
	</svg>
);
const IconRewind = () => (
	<svg
		width="26"
		height="26"
		viewBox="15 31 22 26"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#5e60bf"
			d="M26.0071 37.3297V32.6641L20.175 38.4961L26.0071 44.3282V39.6625C29.8679 39.6625 33.0055 42.8002 33.0055 46.661C33.0055 50.5218 29.8679 53.6594 26.0071 53.6594C22.1462 53.6594 19.0086 50.5218 19.0086 46.661H16.6758C16.6758 51.8165 20.8515 55.9922 26.0071 55.9922C31.1626 55.9922 35.3383 51.8165 35.3383 46.661C35.3383 41.5054 31.1626 37.3297 26.0071 37.3297Z"
		/>
	</svg>
);
const IconForward = () => (
	<svg
		width="26"
		height="26"
		viewBox="267 31 22 26"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#5e60bf"
			d="M277.993 37.3297V32.6641L283.825 38.4961L277.993 44.3282V39.6625C274.132 39.6625 270.994 42.8002 270.994 46.661C270.994 50.5218 274.132 53.6594 277.993 53.6594C281.854 53.6594 284.991 50.5218 284.991 46.661H287.324C287.324 51.8165 283.148 55.9922 277.993 55.9922C272.837 55.9922 268.662 51.8165 268.662 46.661C268.662 41.5054 272.837 37.3297 277.993 37.3297Z"
		/>
	</svg>
);
const IconPrev = () => (
	<svg
		width="16"
		height="16"
		viewBox="77 38 14 15"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#5e60bf"
			d="M78.0059 39.501H80.0056V51.4991H78.0059V39.501ZM81.5053 45.5001L90.004 51.4991V39.501L81.5053 45.5001Z"
		/>
	</svg>
);
const IconNext = () => (
	<svg
		width="16"
		height="16"
		viewBox="213 38 14 15"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#5e60bf"
			d="M225.994 51.499L223.994 51.499L223.994 39.5009L225.994 39.5009L225.994 51.499ZM222.495 45.4999L213.996 39.5009L213.996 51.499L222.495 45.4999Z"
		/>
	</svg>
);
const IconNote = () => (
	<svg
		width="14"
		height="16"
		viewBox="15 8 10 14"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#5e60bf"
			d="M19.9878 9.74194V16.7709C19.5947 16.5444 19.1416 16.4045 18.6552 16.4045C17.1828 16.4045 15.9902 17.5971 15.9902 19.0695C15.9902 20.5419 17.1828 21.7345 18.6552 21.7345C20.1277 21.7345 21.3203 20.5419 21.3203 19.0695V12.407H23.9853V9.74194H19.9878Z"
		/>
	</svg>
);
const IconVolume = () => (
	<svg
		width="36"
		height="36"
		viewBox="0 0 36 36"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#fff"
			fillOpacity="0.1"
			d="M0 17.9944C0 8.05638 8.05638 0 17.9944 0C27.9325 0 35.9888 8.05638 35.9888 17.9944C35.9888 27.9325 27.9325 35.9888 17.9944 35.9888C8.05638 35.9888 0 27.9325 0 17.9944Z"
		/>
		<path
			fill="#5e60bf"
			d="M11.2422 15.7449V20.2435H14.2413L17.9901 23.9923V11.996L14.2413 15.7449H11.2422ZM21.3641 17.9942C21.3641 16.6671 20.5993 15.5274 19.4896 14.9726V21.0082C20.5993 20.4609 21.3641 19.3213 21.3641 17.9942ZM19.4896 11.4187V12.9632C21.6565 13.608 23.2385 15.6174 23.2385 17.9942C23.2385 20.3709 21.6565 22.3803 19.4896 23.0251V24.5696C22.4962 23.8873 24.738 21.2032 24.738 17.9942C24.738 14.7852 22.4962 12.101 19.4896 11.4187Z"
		/>
	</svg>
);

// Muted variant — the speaker glyph with a diagonal slash. Shown when the
// podcast audio is muted so the toggle reads as a clear on/off state.
const IconVolumeMuted = () => (
	<svg
		width="36"
		height="36"
		viewBox="0 0 36 36"
		fill="none"
		aria-hidden="true"
	>
		<path
			fill="#fff"
			fillOpacity="0.1"
			d="M0 17.9944C0 8.05638 8.05638 0 17.9944 0C27.9325 0 35.9888 8.05638 35.9888 17.9944C35.9888 27.9325 27.9325 35.9888 17.9944 35.9888C8.05638 35.9888 0 27.9325 0 17.9944Z"
		/>
		<path
			fill="#9aa0ad"
			d="M11.2422 15.7449V20.2435H14.2413L17.9901 23.9923V11.996L14.2413 15.7449H11.2422Z"
		/>
		<path
			stroke="#9aa0ad"
			strokeWidth="1.6"
			strokeLinecap="round"
			d="M20.5 15.5L25 20M25 15.5L20.5 20"
		/>
	</svg>
);
// Full play/pause button rendered verbatim from Figma node 2335:73122 — the
// #5E60BF circle, the stroked glyph, and the exact dual drop-shadow filter.
// Rendered 1:1 (circle = 72px); the <button> is just a transparent wrapper.
const IconPlay = ({ playing }: { playing: boolean }) => (
	<svg
		width="72"
		height="72"
		viewBox="20 0 72 72"
		fill="none"
		aria-hidden="true"
		style={{ overflow: "visible" }}
	>
		<g filter="url(#pbShadow)">
			<path
				d="M20 35.9944C20 16.1153 36.1153 0 55.9944 0C75.8736 0 91.9889 16.1153 91.9889 35.9944C91.9889 55.8736 75.8736 71.9889 55.9944 71.9889C36.1153 71.9889 20 55.8736 20 35.9944Z"
				fill="#5e60bf"
				shapeRendering="crispEdges"
			/>
			{playing ? (
				<>
					<rect x="49" y="23" width="5" height="26" rx="2.5" fill="#fff" />
					<rect x="58" y="23" width="5" height="26" rx="2.5" fill="#fff" />
				</>
			) : (
				<path
					d="M49.4863 23.9978L68.1479 35.9946L49.4863 47.9913V23.9978Z"
					stroke="#fff"
					strokeWidth="2.66594"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			)}
		</g>
		<defs>
			<filter
				id="pbShadow"
				x="0"
				y="0"
				width="111.988"
				height="111.989"
				filterUnits="userSpaceOnUse"
				colorInterpolationFilters="sRGB"
			>
				<feFlood floodOpacity="0" result="BackgroundImageFix" />
				<feColorMatrix
					in="SourceAlpha"
					type="matrix"
					values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
					result="hardAlpha"
				/>
				<feMorphology
					radius="6"
					operator="erode"
					in="SourceAlpha"
					result="effect1_dropShadow"
				/>
				<feOffset dy="8" />
				<feGaussianBlur stdDeviation="5" />
				<feComposite in2="hardAlpha" operator="out" />
				<feColorMatrix
					type="matrix"
					values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
				/>
				<feBlend
					mode="normal"
					in2="BackgroundImageFix"
					result="effect1_dropShadow"
				/>
				<feColorMatrix
					in="SourceAlpha"
					type="matrix"
					values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
					result="hardAlpha"
				/>
				<feMorphology
					radius="5"
					operator="erode"
					in="SourceAlpha"
					result="effect2_dropShadow"
				/>
				<feOffset dy="20" />
				<feGaussianBlur stdDeviation="12.5" />
				<feComposite in2="hardAlpha" operator="out" />
				<feColorMatrix
					type="matrix"
					values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
				/>
				<feBlend
					mode="normal"
					in2="effect1_dropShadow"
					result="effect2_dropShadow"
				/>
				<feBlend
					mode="normal"
					in="SourceGraphic"
					in2="effect2_dropShadow"
					result="shape"
				/>
			</filter>
		</defs>
	</svg>
);

/**
 * Podcast player. Figma node 2420:25570.
 *
 * Streams from the presigned URL `detail.podcastUrl` (S3 mp3 written by the
 * prism podcast worker). Until the worker lands, podcastUrl is null and we
 * show a pending state.
 *
 * era-api does not expose the prism PodcastMeta sidecar (no DB column), so per
 * the SP-A spec we DERIVE the episode meta client-side from the article intro
 * (About) and card questions ("In this episode"); the host is a static
 * placeholder. See `lib/edhub/podcastMeta`.
 */
// Persist playback progress at most this often while playing; pause / ended /
// unmount always flush immediately so we never lose the last position.
const PROGRESS_SAVE_INTERVAL_MS = 15_000;

export function EducationPodcast() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	// Resume position handed in by the Resume carousel via router state.
	const resumeAt = (location.state as { resumeAt?: number } | null)?.resumeAt;
	const { logEducation } = useAnalytics();
	const { t, locale } = useClientTranslations("education");
	const [detail, setDetail] = useState<EducationHubArticleDetail | null>(null);
	const [artefacts, setArtefacts] = useState<{
		article: Article | null;
		deck: CardDeck | null;
	}>({ article: null, deck: null });
	// Derived (not fetched) per locale: only the static host card is
	// locale-dependent, so a language switch must not refetch the artefacts.
	const meta: DerivedPodcastMeta = useMemo(
		() => derivePodcastMeta(artefacts.article, artefacts.deck, locale),
		[artefacts, locale],
	);
	const [playing, setPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [current, setCurrent] = useState(0);
	const [speedIdx, setSpeedIdx] = useState(0);
	const [isMuted, setIsMuted] = useState(false);
	const [audioError, setAudioError] = useState(false);
	const [audioSrc, setAudioSrc] = useState<string | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	// Latest playback position, kept in a ref so the unmount cleanup can flush
	// it without re-subscribing the effect on every tick.
	const progressRef = useRef({ current: 0, duration: 0 });
	const lastSaveRef = useRef(0);

	// Upsert the current podcast position to era-api. Returns the in-flight
	// request so callers that navigate away (close) can await it — otherwise the
	// fire-and-forget PUT races the Hub's GET /progress and the resume card shows
	// a stale (one-session-behind) position. A failed save never disrupts the UI.
	// No-op until we know the duration.
	const saveProgress = (): Promise<void> => {
		// Prefer the live element so a direct seek (scrubber/skip) that hasn't
		// emitted a timeupdate yet is still persisted; fall back to the ref when
		// the element is gone (unmount). Clamp to [0, duration] so an over-long
		// position can never be POSTed.
		const el = audioRef.current;
		const duration =
			el && Number.isFinite(el.duration) && el.duration > 0
				? el.duration
				: progressRef.current.duration;
		if (!id || duration <= 0) return Promise.resolve();
		const raw = el ? el.currentTime : progressRef.current.current;
		const current = Math.min(Math.max(0, raw), duration);
		const progress = Math.min(1, Math.max(0, current / duration));
		return savePodcastProgress(id, { progress, currentTime: current }).catch(
			() => {},
		);
	};

	// Flush the position when leaving the player OR when the content id changes
	// without unmounting (React Router reuses this component across
	// `/education/:id/podcast` params). Keying on `id` makes the cleanup capture
	// the correct id, so we never PUT under a stale content id. saveProgress
	// reads refs for the position; id is the only meaningful dependency.
	// biome-ignore lint/correctness/useExhaustiveDependencies: flush uses current id; saveProgress is a stable-enough closure
	useEffect(() => {
		// Backstop for non-close exits (back gesture, id switch); fire-and-forget.
		return () => {
			void saveProgress();
		};
	}, [id]);

	// Resolve the auth-gated proxy cover image to a bearer-fetched blob (presigned
	// URLs pass through); loading/error/idle → gradient shows; loaded → hero.
	const { url: coverUrl } = useResolvedImageUrl(detail?.imageUrl);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current) return;
		hasTrackedPageView.current = true;
		logEducation("podcast_page_viewed", { contentId: id });
	}, [logEducation, id]);

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		fetchArticleDetail(id)
			.then(async (d) => {
				if (cancelled) return;
				setDetail(d);
				const [article, deck] = await Promise.all([
					d.articleUrl
						? fetchArticle(d.articleUrl).catch(() => null)
						: Promise.resolve<Article | null>(null),
					d.cardsUrl
						? fetchCardDeck(d.cardsUrl).catch(() => null)
						: Promise.resolve<CardDeck | null>(null),
				]);
				if (!cancelled) setArtefacts({ article, deck });
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [id]);

	// Proxy-mode podcast URLs are auth-gated relative paths a native <audio>
	// can't load directly; resolveMediaUrl fetches them with the bearer token
	// and returns a blob object URL (revoked on cleanup). Presigned S3 URLs
	// pass through unchanged.
	useEffect(() => {
		const url = detail?.podcastUrl;
		if (!url) return;
		let revoked: string | null = null;
		let cancelled = false;
		setAudioError(false);
		resolveMediaUrl(url)
			.then((resolved) => {
				if (cancelled) {
					if (resolved.startsWith("blob:")) URL.revokeObjectURL(resolved);
					return;
				}
				if (resolved.startsWith("blob:")) revoked = resolved;
				setAudioSrc(resolved);
			})
			.catch(() => {
				if (!cancelled) setAudioError(true);
			});
		return () => {
			cancelled = true;
			if (revoked) URL.revokeObjectURL(revoked);
		};
	}, [detail?.podcastUrl]);

	const fmt = (sec: number) => {
		if (!Number.isFinite(sec)) return "--:--";
		const m = Math.floor(sec / 60)
			.toString()
			.padStart(2, "0");
		const s = Math.floor(sec % 60)
			.toString()
			.padStart(2, "0");
		return `${m}:${s}`;
	};

	const togglePlay = () => {
		const el = audioRef.current;
		if (!el) return;
		if (playing) {
			el.pause();
			setPlaying(false);
		} else {
			void el.play();
			setPlaying(true);
		}
		logEducation("podcast_play_toggled", {
			contentId: id,
			playing: !playing,
			current_time: Math.round(el.currentTime),
		});
	};

	const skip = (sec: number) => {
		const el = audioRef.current;
		if (!el) return;
		el.currentTime = Math.max(
			0,
			Math.min(el.duration || 0, el.currentTime + sec),
		);
	};

	const cycleSpeed = () => {
		const next = (speedIdx + 1) % SPEEDS.length;
		setSpeedIdx(next);
		const el = audioRef.current;
		if (el) el.playbackRate = SPEEDS[next];
	};

	const toggleMute = () => {
		setIsMuted((m) => !m);
	};

	// Keep the audio element in sync with `isMuted` — including when the element
	// mounts/changes after the user already toggled mute (audioSrc resolves
	// async, so audioRef can be null at toggle time). audioSrc is in the deps on
	// purpose: it's the signal that the <audio> has (re)mounted, since
	// audioRef.current itself isn't reactive.
	// biome-ignore lint/correctness/useExhaustiveDependencies: audioSrc re-runs the sync when the <audio> element mounts.
	useEffect(() => {
		const el = audioRef.current;
		if (el) el.muted = isMuted;
	}, [isMuted, audioSrc]);

	return (
		<EdHubDetailShell
			availableFormats={["text", "cards", "audio"]}
			onFormatSelect={(format) =>
				id && navigate(`/education/${id}/${formatToSegment(format)}`)
			}
			onClose={async () => {
				// Flush the latest position and wait for the PUT to commit before
				// returning to the Hub, so its GET /progress reads the fresh value.
				await saveProgress();
				navigate("/education");
			}}
			closeLabel={t("article.close", "Close")}
		>
			<div className={styles.content}>
				{!detail && (
					<p className={styles.loading}>{t("article.loading", "Loading…")}</p>
				)}

				{detail && !detail.podcastUrl && (
					<div className={styles.pending}>
						<p>
							{t(
								"podcast.pending",
								"The podcast for this article is still being generated.",
							)}
						</p>
					</div>
				)}

				{detail?.podcastUrl && (
					<div className={styles.playerCard}>
						<div
							className={styles.cover}
							style={
								coverUrl
									? {
											// Hero image first, .cover gradient placeholder second — a
											// failed hero load keeps the dark placeholder so the white
											// title/badges stay legible.
											backgroundImage: `url(${coverUrl}), linear-gradient(180deg, #5a5fbf 0%, #2a2f5c 100%)`,
											backgroundSize: "cover",
										}
									: undefined
							}
						>
							<div className={styles.coverOverlay} aria-hidden="true" />
							<div className={styles.coverContent}>
								<div className={styles.coverTitle}>{detail.title}</div>
								<div className={styles.coverBadges}>
									<span className={styles.badge}>
										<IconClock />
										{t("podcast.episode", "Podcast")}
									</span>
									{duration > 0 && (
										<span className={styles.badge}>
											<IconClock />
											{`${Math.round(duration / 60)} min`}
										</span>
									)}
								</div>
							</div>
						</div>

						{audioSrc && (
							// biome-ignore lint/a11y/useMediaCaption: derived podcast audio has no caption track yet (no transcript artefact); pending SP-B
							<audio
								ref={audioRef}
								src={audioSrc}
								preload="metadata"
								onLoadedMetadata={(e) => {
									const el = e.currentTarget as HTMLAudioElement;
									setDuration(el.duration);
									progressRef.current.duration = el.duration;
									setAudioError(false);
									// Resume where the user left off (from the Resume carousel),
									// clamped to the media length so a stale/over-long position
									// can't be stored or POSTed.
									if (resumeAt && resumeAt > 0) {
										const at = Number.isFinite(el.duration)
											? Math.min(resumeAt, el.duration)
											: resumeAt;
										el.currentTime = at;
										setCurrent(at);
										progressRef.current.current = at;
									}
								}}
								onTimeUpdate={(e) => {
									const t = (e.currentTarget as HTMLAudioElement).currentTime;
									setCurrent(t);
									progressRef.current.current = t;
									const now = Date.now();
									if (now - lastSaveRef.current >= PROGRESS_SAVE_INTERVAL_MS) {
										lastSaveRef.current = now;
										saveProgress();
									}
								}}
								onPause={() => saveProgress()}
								onEnded={() => {
									setPlaying(false);
									saveProgress();
									logEducation("podcast_ended", { contentId: id });
								}}
								onError={() => setAudioError(true)}
							/>
						)}

						{audioError && (
							<p className={styles.audioError}>
								{t(
									"podcast.audioError",
									"This episode could not be loaded. You can still read about it below.",
								)}
							</p>
						)}

						<div className={styles.timeline}>
							<input
								type="range"
								min={0}
								max={duration || 0}
								step={1}
								value={current}
								onChange={(e) => {
									const el = audioRef.current;
									if (el) el.currentTime = Number(e.target.value);
								}}
								className={styles.scrubber}
								style={{
									background: `linear-gradient(to right, #5e60bf ${
										duration > 0 ? (current / duration) * 100 : 0
									}%, #bdbeff ${
										duration > 0 ? (current / duration) * 100 : 0
									}%)`,
								}}
								aria-label={t("podcast.scrubber", "Seek")}
							/>
							<div className={styles.times}>
								<span>{fmt(current)}</span>
								<span>{fmt(duration)}</span>
							</div>
						</div>

						<div className={styles.playControl}>
							<button
								type="button"
								className={styles.ctlSkip}
								onClick={() => skip(-10)}
								aria-label={t("podcast.skipBack", "Skip back 10 seconds")}
							>
								<IconRewind />
							</button>
							<button
								type="button"
								className={styles.ctlTrack}
								onClick={() => {
									const el = audioRef.current;
									if (el) el.currentTime = 0;
								}}
								aria-label={t("podcast.prevEpisode", "Restart episode")}
							>
								<IconPrev />
							</button>
							<button
								type="button"
								className={styles.ctlBig}
								onClick={togglePlay}
								aria-label={
									playing
										? t("podcast.pause", "Pause")
										: t("podcast.play", "Play")
								}
							>
								<IconPlay playing={playing} />
							</button>
							{/* Episode-to-episode navigation is deferred — disable rather
							    than advertise a no-op control to assistive tech. */}
							<button
								type="button"
								className={styles.ctlTrack}
								disabled
								aria-label={t("podcast.nextEpisode", "Next episode")}
							>
								<IconNext />
							</button>
							<button
								type="button"
								className={styles.ctlSkip}
								onClick={() => skip(10)}
								aria-label={t("podcast.skipForward", "Skip forward 10 seconds")}
							>
								<IconForward />
							</button>
						</div>

						<div className={styles.extras}>
							<button
								type="button"
								className={styles.speedBtn}
								onClick={cycleSpeed}
								aria-label={t("podcast.speed", "Playback speed")}
							>
								<IconNote />
								{`${SPEEDS[speedIdx].toFixed(1)}x`}
							</button>
							<div className={styles.volume}>
								<button
									type="button"
									className={styles.volumeBtn}
									onClick={toggleMute}
									aria-label={
										isMuted
											? t("podcast.unmute", "Unmute")
											: t("podcast.mute", "Mute")
									}
									aria-pressed={isMuted}
								>
									{isMuted ? <IconVolumeMuted /> : <IconVolume />}
								</button>
							</div>
						</div>
					</div>
				)}

				{detail && (
					<>
						<section className={styles.aboutCard}>
							<div className={styles.aboutHeader}>
								<div className={styles.aboutIcon} aria-hidden="true">
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="white"
										strokeWidth={2}
										width={24}
										height={24}
										role="img"
										aria-label="Headphones"
									>
										<path d="M3 14v-2a9 9 0 0 1 18 0v2" />
										<rect x={3} y={14} width={4} height={6} rx={1} />
										<rect x={17} y={14} width={4} height={6} rx={1} />
									</svg>
								</div>
								<div className={styles.aboutHeaderText}>
									<h2 className={styles.cardHeading}>
										{t("podcast.about", "About This Episode")}
									</h2>
									{detail.publishedAt && (
										<p className={styles.aboutDate}>
											{new Date(detail.publishedAt).toLocaleDateString(locale, {
												year: "numeric",
												month: "long",
												day: "numeric",
											})}
										</p>
									)}
								</div>
							</div>
							<p className={styles.aboutBody}>{meta.description}</p>

							{meta.bullets.length > 0 && (
								<>
									<h3 className={styles.sectionHeading}>
										{t("podcast.inThisEpisode", "In this episode:")}
									</h3>
									<ul className={styles.episodeList}>
										{meta.bullets.map((b) => (
											<li key={b}>{b}</li>
										))}
									</ul>
								</>
							)}
						</section>

						<section className={styles.hostCard}>
							<div className={styles.hostRow}>
								<div className={styles.hostAvatar} aria-hidden="true">
									{meta.host.initials}
								</div>
								<div className={styles.hostText}>
									<p className={styles.hostName}>{meta.host.name}</p>
									<p className={styles.hostRole}>{meta.host.role}</p>
								</div>
							</div>
							<p className={styles.hostBio}>
								{meta.host.bio ||
									t(
										"podcast.hostBio",
										"This episode is based on the article and walks you through it in plain language.",
									)}
							</p>
						</section>
					</>
				)}
			</div>
		</EdHubDetailShell>
	);
}
